import React, {
  Context,
  createContext,
  useContext,
  useSyncExternalStore,
} from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  cacheKeys,
  useSignin,
  useSignout,
  useSignup,
  useSignupLight,
} from "../services/queries"
import StoreContext from "./store-context"
import { AuthenticateRequest } from "../models/email-authenticate-request"
import { SignupLightPayload, SignupPayload } from "../models/sign-up-form"
import { removeCachedOrder } from "../services/cached-order"
import {
  AccountType,
  getCachedAccountType,
  getCachedUser,
  removeCachedUser,
  setCachedUser,
  subscribeCachedUser,
} from "../services/cached-user"
import { ensureGuestSession } from "../services/request"

type AccountContextType = {
  accountType: AccountType
  signinGuestAndClearCaches: () => void
  signinUser: (payload: AuthenticateRequest) => void
  signoutAndClearCaches: () => void
  signupAndSignin: (payload: SignupPayload) => void
  signupLightAndSignin: (payload: SignupLightPayload) => Promise<void>
}

type DefaultAccountContextType = {
  accountType?: AccountType
  signinGuestAndClearCaches: () => void
  signinUser: (payload: AuthenticateRequest) => void
  signoutAndClearCaches: () => void
  signupAndSignin: (payload: SignupPayload) => void
  signupLightAndSignin: (payload: SignupLightPayload) => Promise<void>
}

const defaultContext: DefaultAccountContextType = {
  accountType: undefined,
  signinGuestAndClearCaches: () => {},
  signinUser: () => {},
  signoutAndClearCaches: () => {},
  signupAndSignin: () => {},
  signupLightAndSignin: () => Promise.resolve(),
}

export const AccountContext = createContext(defaultContext)

type Props = {
  children: React.ReactNode
}

const getServerAccountType = (): AccountType => "guest"

export const AccountProvider = ({ children }: Props) => {
  // Derived from the cached user rather than kept in state: the fetch layer
  // mints guest sessions on its own, and this is how those reach the UI.
  // There is no bootstrap step - a request that finds no token waits for a
  // guest session itself (see useFetchRequest).
  const accountType = useSyncExternalStore(
    subscribeCachedUser,
    getCachedAccountType,
    getServerAccountType
  )
  const { clearCart, setDeliveryAddress } = useContext(StoreContext)
  const { mutateAsync: signin } = useSignin()
  const { mutateAsync: signout } = useSignout()
  const { mutateAsync: signup } = useSignup()
  const { mutateAsync: signupLight } = useSignupLight()
  const queryClient = useQueryClient()

  const signinGuestAndClearCaches = async () => {
    clearCart()
    removeCachedUser()
    // Purchases and receipt links must not outlive the account they belong to
    queryClient.removeQueries({ queryKey: [cacheKeys.paymentHistory] })
    localStorage?.removeItem("hg::cache::cart")
    localStorage?.removeItem("hg::cache::order")

    await ensureGuestSession()
  }

  const signinUser = async (payload: AuthenticateRequest) => {
    queryClient.removeQueries({ queryKey: [cacheKeys.paymentHistory] })

    // A guest's order cannot be transferred to the user, so it is dropped.
    // A user signing in again after their session was refused keeps theirs:
    // the order belongs to the account, not the session.
    const previousWasGuest =
      getCachedUser() !== null && getCachedAccountType() === "guest"

    const { data } = await signin(payload)
    if (previousWasGuest) {
      removeCachedOrder()
    }
    setDeliveryAddress(null) // TODO: Unite cache clearance in its own function. Refactor clearCart function from StoreContext
    setCachedUser(data)
  }

  const signoutAndClearCaches = async () => {
    await signout()
    window?.analytics?.track("Logout")
    await signinGuestAndClearCaches()
  }

  const signupAndSignin = async (payload: SignupPayload) => {
    await signup(payload)
    window?.analytics?.track("User sign up")

    await signinUser({
      username: payload.email,
      password: payload.password,
    })
  }

  const signupLightAndSignin = async (payload: SignupLightPayload) => {
    await signupLight(payload)
    window?.analytics?.track("User sign up")

    await signinUser({
      username: payload.email,
      password: payload.password,
    })
  }

  return (
    <AccountContext.Provider
      value={{
        accountType,
        signinGuestAndClearCaches,
        signinUser,
        signoutAndClearCaches,
        signupAndSignin,
        signupLightAndSignin,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export const useAccount = () => {
  return useContext<AccountContextType>(
    AccountContext as Context<AccountContextType>
  )
}
